import { useCallback, useEffect, useState } from "react";
import { fetchHotWalletDetail, type ApiErrorInfo, type ApiHotWallet } from "@/lib/api";

interface UseHotWalletDetailResult {
  wallet: ApiHotWallet | null;
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

/** Detail hook for a single hot wallet, used by HW-S1's per-wallet view and
 * as the shared identity for HW-S2/S4's wallet-scoped sub-pages. */
export function useHotWalletDetail(walletId: string | undefined): UseHotWalletDetailResult {
  const [wallet, setWallet] = useState<ApiHotWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);

  const load = useCallback(async () => {
    if (!walletId) {
      setWallet(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHotWalletDetail(walletId);
      if (result.error) {
        setWallet(null);
        setError(result.error);
      } else {
        setWallet(result.item);
      }
    } catch {
      setWallet(null);
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      setLoading(false);
    }
  }, [walletId]);

  useEffect(() => {
    load();
  }, [load]);

  return { wallet, loading, error, refresh: load };
}
